import { NgFor, NgIf } from '@angular/common';
import { Component, inject} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StoredAlbum, StoredAudio } from '@shared/types';
import { DatabaseService } from '../../services/database.service';
import { StorageService } from 'src/app/services/storage.service';

@Component({
  selector: 'app-manage-audios',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgFor,
    MatProgressSpinnerModule,
    MatIcon
  ],
  templateUrl: './manage-audios.component.html',
  styleUrl: './manage-audios.component.scss'
})
export class ManageAudiosComponent {

  ready = false;

  audios: StoredAudio[] = [];


  constructor(
    private db: DatabaseService,
    private dialog: MatDialog
  ) {
    this.loadAudioList();
  }

  private async loadAudioList() {
    this.audios = await this.db.getAudios();
    this.audios.forEach( audio => { if (audio.on === undefined) audio.on = true; });
    this.ready = true;
  };

  editRecord(index: number) {
    const data = {
      mode: 'edit',
      index: index, 
      audios: this.audios
    };
    const dialogRef = this.dialog.open(ManageAudiosDialog, {data});

    dialogRef.afterClosed().subscribe(result => {
      console.log('result', result);
      if ( result )
        this.audios = result;
    });
  }


  removeRecord(index: number) {
    const title = this.audios[index].title;
    const result = confirm(`Do you really want to remove audio \'${title}\'?`);
    if ( result ) {
      this.audios.splice(index, 1);
      this.db.setAudios(this.audios);
    }
  }

  public reorderRecord(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const length = this.audios.length
    if (newIndex >= 0 && newIndex < length) {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      const movedAudio = this.audios.splice(index, 1)[0];
      this.audios.splice(newIndex, 0, movedAudio);
    }
    this.db.setAudios(this.audios);
  }


  addRecord(index: number) {
    const data = {
      mode: 'add',
      index: index, 
      audios: this.audios
    };
    const dialogRef = this.dialog.open(ManageAudiosDialog, {data});
    dialogRef.afterClosed().subscribe(
      (audios: StoredAudio[] | null) => {
      if ( audios ) {
        this.audios = audios;
      }
    });
  }

}







// MANAGE-AUDIOS DIALOG
@Component({
  selector: 'manage-audios-dialog',
  templateUrl: 'manage-audios-dialog.html',
  styleUrl: './manage-audios.component.scss',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
})
export class ManageAudiosDialog {

  readonly dialogRef = inject(MatDialogRef);
  readonly data = inject<{
    mode: 'add' | 'edit',
    index: number,
    audios: StoredAudio[]
  }>(MAT_DIALOG_DATA);

  albums: StoredAlbum[] = [];

  editForm: FormGroup;
  uploading = false;
  loadingAudio = false;

  fileSrc: string = '';
  audioFileTitle: string = '';
  file: File | null = null; 

  constructor(
    private db: DatabaseService,
    private storage: StorageService,
    public formBuilder: FormBuilder
  ) {
    //console.log('data', this.data);
    this.editForm = this.formBuilder.group({
      on: ['', Validators.required],
      url: ['', Validators.required],
      title: [''],
      author: [''],
      albumImgUrl: [''],
      description: ['']
    });

    if ( this.data.mode === 'edit' ) {
      const audio = this.data.audios[this.data.index];
      this.editForm.setValue({
        on: audio.on,
        url: audio.url,
        title: audio.title,
        author: audio.author || '',
        albumImgUrl: audio.albumImgUrl || '',
        description: audio.description || ''
      });
      this.audioFileTitle = this.estraiNomeFile(audio.url)
      this.loadingAudio = true;
    }

    this.loadAlbumList();
  }


  private async loadAlbumList() {
    this.albums = await this.db.getAlbums();
  }

  private estraiNomeFile(url: string): string {
    try {
      const urlObj = new URL(url);
      // La parte 'o/my%2Fpath%2Ffile.ext' si trova nel pathname
      const pathname = decodeURIComponent(urlObj.pathname);
      // Firebase Storage aggiunge '/o/' nel pathname, quindi rimuoviamo '/o/'
      const percorso = pathname.replace(/^\/o\//, '');
      const segmenti = percorso.split('/');
      return segmenti.pop() || '';
    } catch (error) {
      console.error('URL non valido:', error);
      return '';
    }
  }

  preventSubmit(event: KeyboardEvent): void {
    if (event instanceof KeyboardEvent && event.key === 'Enter') {
      event.preventDefault(); // Impedisce il comportamento di default
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.file = target.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.fileSrc = e.target.result; // Imposta l'immagine selezionata come nuova sorgente
        this.audioFileTitle = this.file?.name || '';
        this.loadingAudio = true;
      };
      reader.readAsDataURL(this.file); // Legge il file come Data URL
    }
  }

  onImageLoad(): void {
    this.loadingAudio = false;
  }

  public async saveRecord() {
    if ( this.data.mode === 'edit') {
      const updatedAudio: StoredAudio = this.editForm.value;
      this.data.audios[this.data.index] = updatedAudio;
    } 
    else { // mode === 'add'
      if (this.file) {
        try {
          this.uploading = true;
          const url = await this.storage.uploadImageAndGetUrl('audios',this.file);
          this.editForm.value.url = url;
          const newAudio: StoredAudio = this.editForm.value;
          this.data.audios.splice(this.data.index, 0, newAudio);
        } catch (error) {
          console.error('Errore durante il caricamento:', error);
        } finally {
          this.uploading = false;
        }
      }
    }
    await this.db.setAudios(this.data.audios)
      .then( () => {
        this.dialogRef.close(this.data.audios);
      })
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

}
